import React, { useEffect, useState, useCallback } from 'react';
import { FaCheckCircle, FaSpinner, FaExclamationTriangle } from 'react-icons/fa';
import { MercadoPagoService } from '../services/MercadoPagoService';
import { parsePaymentReturn } from '../services/paymentReturn';

const NOMES_PLANO = {
  essential: 'Essencial',
  professional: 'Profissional',
  management: 'Gestão',
  premium: 'Premium',
  pro: 'Pro',
  addon_irrigacao: 'Add-on Irrigação',
  addon_satelite: 'Add-on Satélite',
};

export default function PagamentoSucesso({ onVoltar }) {
  const [fase, setFase] = useState('verificando'); // verificando | ativando | sucesso | erro | pago_sem_ativacao
  const [tentativa, setTentativa] = useState(0);
  const [mensagemErro, setMensagemErro] = useState('');
  const [nomePlano, setNomePlano] = useState('');
  const [detalhesAssinatura, setDetalhesAssinatura] = useState(null);
  const MAX_TENTATIVAS = 8;

  const verificar = useCallback(async () => {
    const paymentReturn = parsePaymentReturn();
    const subscriptionId = paymentReturn.subscriptionId
      || window.sessionStorage.getItem('climorax:pending-subscription-id');
    const extRef = paymentReturn.externalReference;

    // Descobre o nome do plano para exibir
    try {
      const ref = JSON.parse(extRef || '{}');
      setNomePlano(NOMES_PLANO[ref.plano] || ref.plano || 'plano');
    } catch (_) {}

    if (!subscriptionId) {
      setMensagemErro('Identificador da assinatura não encontrado. Volte aos planos e confira o status.');
      setFase('erro');
      return;
    }

    // O Mercado Pago pode redirecionar após autorizar o cartão, enquanto a
    // primeira cobrança ainda está sendo processada pelo webhook.
    for (let i = 0; i < MAX_TENTATIVAS; i++) {
      setTentativa(i + 1);
      setFase(i === 0 ? 'verificando' : 'ativando');

      try {
        const resultado = await MercadoPagoService.consultarAssinatura(subscriptionId);
        setNomePlano(NOMES_PLANO[resultado.produto] || resultado.produto || 'plano');
        setDetalhesAssinatura(resultado);

        if (resultado.ativa) {
          setFase('sucesso');
          window.sessionStorage.removeItem('climorax:pending-subscription-id');
          window.sessionStorage.removeItem('climorax:pending-subscription-environment');
          // Redireciona para o app após 3s via callback (sem reload)
          setTimeout(() => onVoltar && onVoltar(), 3000);
          return;
        }

        if (resultado.status === 'authorized' || resultado.status === 'pending') {
          if (i < MAX_TENTATIVAS - 1) {
            await new Promise(r => setTimeout(r, 3000));
            continue;
          }
          // Esgotou tentativas mas pagamento foi recebido
          setFase('pago_sem_ativacao');
          return;
        }

        setMensagemErro(`Assinatura com status: ${resultado.status}. Nenhuma nova cobrança será feita.`);
        setFase('erro');
        return;

      } catch (err) {
        console.error('Erro ao verificar pagamento (tentativa', i + 1, '):', err);
        if (i < MAX_TENTATIVAS - 1) {
          await new Promise(r => setTimeout(r, 3000));
        } else {
          setMensagemErro('Não foi possível confirmar o pagamento. Se o valor foi cobrado, entre em contato com o suporte.');
          setFase('erro');
        }
      }
    }
  }, [onVoltar]);

  useEffect(() => {
    verificar();
  }, [verificar]);

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: 20
    }}>
      <main
        role="status"
        aria-live="polite"
        aria-busy={fase === 'verificando' || fase === 'ativando'}
        style={{
        background: '#fff',
        borderRadius: 20,
        padding: 40,
        maxWidth: 480,
        width: '100%',
        textAlign: 'center',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
      }}>

        {/* VERIFICANDO / ATIVANDO */}
        {(fase === 'verificando' || fase === 'ativando') && (
          <>
            <FaSpinner aria-hidden="true" style={{ fontSize: 56, color: '#667eea', animation: 'spin 1s linear infinite' }} />
            <h1 style={{ marginTop: 20, color: '#333' }}>
              {fase === 'verificando' ? 'Confirmando pagamento…' : 'Ativando seu plano…'}
            </h1>
            <p style={{ color: '#888', fontSize: 15, marginTop: 8 }}>
              {tentativa > 1 ? `Tentativa ${tentativa} de ${MAX_TENTATIVAS}…` : 'Aguarde um instante.'}
            </p>
            <div style={{
              marginTop: 24, height: 6, background: '#e9e9e9', borderRadius: 3, overflow: 'hidden'
            }} role="progressbar" aria-label="Confirmação da assinatura" aria-valuemin="0" aria-valuemax={MAX_TENTATIVAS} aria-valuenow={tentativa}>
              <div style={{
                height: '100%',
                width: `${(tentativa / MAX_TENTATIVAS) * 100}%`,
                background: 'linear-gradient(90deg, #667eea, #764ba2)',
                borderRadius: 3,
                transition: 'width 0.5s ease'
              }} />
            </div>
          </>
        )}

        {/* SUCESSO */}
        {fase === 'sucesso' && (
          <>
            <FaCheckCircle aria-hidden="true" style={{ fontSize: 64, color: '#2e7d32' }} />
            <h1 style={{ marginTop: 20, color: '#333' }}>Tudo certo!</h1>
            <p style={{ color: '#555', fontSize: 16, marginTop: 8 }}>
              {nomePlano ? `${nomePlano} ativado com cobrança recorrente.` : 'Sua assinatura foi ativada com sucesso.'}
            </p>
            {detalhesAssinatura && (
              <div style={{ marginTop: 18, padding: 14, border: '1px solid #d8e7df', borderRadius: 10, background: '#f3faf6', color: '#426454', fontSize: 13, lineHeight: 1.6, textAlign: 'left' }}>
                <div><strong>Periodicidade:</strong> {detalhesAssinatura.periodicidade === 'anual' ? 'Anual' : 'Mensal'}</div>
                <div><strong>Valor recorrente:</strong> {Number(detalhesAssinatura.valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</div>
                {detalhesAssinatura.proxima_cobranca && <div><strong>Próxima cobrança:</strong> {new Date(detalhesAssinatura.proxima_cobranca).toLocaleDateString('pt-BR')}</div>}
                <div><strong>Assinatura:</strong> {detalhesAssinatura.id}</div>
              </div>
            )}
            <p style={{ color: '#999', fontSize: 14, marginTop: 10 }}>Redirecionando em 3 segundos…</p>
          </>
        )}

        {/* PAGO MAS NÃO ATIVADO AINDA */}
        {fase === 'pago_sem_ativacao' && (
          <>
            <div style={{ fontSize: 56 }}>⏳</div>
            <h1 style={{ marginTop: 20, color: '#333' }}>Cartão autorizado</h1>
            <p style={{ color: '#555', fontSize: 15, marginTop: 8, lineHeight: 1.6 }}>
              A assinatura foi criada e a primeira cobrança está sendo processada.
              O acesso será liberado automaticamente após a aprovação.
            </p>
            <button
              onClick={() => onVoltar && onVoltar()}
              style={{
                marginTop: 24, background: '#667eea', color: '#fff', border: 'none',
                borderRadius: 10, padding: '12px 28px', fontSize: 15, fontWeight: 600, cursor: 'pointer'
              }}
            >
              Voltar ao App
            </button>
          </>
        )}

        {/* ERRO */}
        {fase === 'erro' && (
          <>
            <FaExclamationTriangle aria-hidden="true" style={{ fontSize: 56, color: '#b45309' }} />
            <h1 style={{ marginTop: 20, color: '#333' }}>Atenção</h1>
            <p style={{ color: '#666', fontSize: 15, marginTop: 8, lineHeight: 1.6 }}>{mensagemErro}</p>
            <button
              onClick={() => onVoltar && onVoltar()}
              style={{
                marginTop: 24, background: '#667eea', color: '#fff', border: 'none',
                borderRadius: 10, padding: '12px 28px', fontSize: 15, fontWeight: 600, cursor: 'pointer'
              }}
            >
              Voltar ao App
            </button>
          </>
        )}
      </main>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @media (prefers-reduced-motion: reduce) {
          svg { animation: none !important; }
        }
      `}</style>
    </div>
  );
}

